/**
 * Polling client-side qui surveille la table `payment_deletion_audit` pour
 * les 2 superviseurs (Daniel, Bedia) et affiche une modale modale + son
 * dès qu'une nouvelle suppression d'acompte est détectée. La modale ne peut
 * être fermée qu'en cliquant explicitement sur l'un des deux boutons -
 * c'est l'acquittement qui met à jour le `lastSeenAt` en localStorage.
 */

import { useAuth } from '@/contexts/AuthContext';
import { ROUTES } from '@/constants/routes';
import {
  paymentDeletionAuditService,
  type PaymentDeletionAuditRow,
} from '@/services/paymentDeletionAuditService';
import { Button, LuxModal, LuxModalBody, LuxModalFooter, LuxModalHeader } from '@components/ui';
import { ShieldExclamationIcon } from '@heroicons/react/24/outline';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const SUPERVISOR_EMAILS = ['daniel.gosset@hotmail.com', 'bedia@eurodecor-meubles.eu'];
const POLL_INTERVAL_MS = 10000;
const LS_KEY = (email: string) => `payment-deletion-last-seen-${email}`;

function playAlertSound() {
  try {
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    const playNote = (freq: number, start: number, dur: number, vol = 0.25) => {
      const osc = audioContext.createOscillator();
      const gain = audioContext.createGain();
      osc.connect(gain);
      gain.connect(audioContext.destination);
      osc.frequency.value = freq;
      osc.type = 'sine';
      gain.gain.setValueAtTime(0, start);
      gain.gain.linearRampToValueAtTime(vol, start + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.01, start + dur);
      osc.start(start);
      osc.stop(start + dur);
    };
    const now = audioContext.currentTime;
    playNote(523.25, now, 0.15);
    playNote(659.25, now + 0.12, 0.15);
    playNote(783.99, now + 0.24, 0.3);
  } catch (_) {}
}

function showBrowserNotification(rows: PaymentDeletionAuditRow[]) {
  if (!('Notification' in window) || Notification.permission !== 'granted') return;
  const title =
    rows.length === 1
      ? "Suppression d'acompte détectée"
      : `${rows.length} suppressions d'acompte détectées`;
  const first = rows[0];
  const amountFmt = new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
  }).format(Number(first.amount) || 0);
  const body =
    rows.length === 1
      ? `${amountFmt} sur ${first.order_number || 'BC ?'} par ${first.deleted_by_full_name || first.deleted_by_email || '?'}`
      : `Premier : ${amountFmt} sur ${first.order_number || 'BC ?'}`;
  try {
    new Notification(title, { body, icon: '/favicon.ico' });
  } catch (_) {}
}

const formatPrice = (n: number) =>
  new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(n);

const formatDateTime = (iso: string) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const PaymentDeletionWatcher: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [unseen, setUnseen] = useState<PaymentDeletionAuditRow[]>([]);
  const knownIdsRef = useRef<Set<string>>(new Set());
  const notifiedIdsRef = useRef<Set<string>>(new Set());

  const isSupervisor = !!user?.email && SUPERVISOR_EMAILS.includes(user.email);

  useEffect(() => {
    if (!isSupervisor || !user?.email) return;

    const email = user.email;

    // Initialisation : si pas de lastSeen en localStorage, démarrer à now()
    // pour ne pas déclencher rétroactivement sur l'historique.
    if (!localStorage.getItem(LS_KEY(email))) {
      localStorage.setItem(LS_KEY(email), new Date().toISOString());
    }

    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      const lastSeen = localStorage.getItem(LS_KEY(email)) || new Date().toISOString();
      try {
        const rows = await paymentDeletionAuditService.getSince(lastSeen);
        if (cancelled) return;

        const newRows = rows.filter(r => !knownIdsRef.current.has(r.id));
        if (newRows.length > 0) {
          newRows.forEach(r => knownIdsRef.current.add(r.id));
          playAlertSound();
          if (document.visibilityState === 'hidden') {
            const notifyRows = newRows.filter(r => !notifiedIdsRef.current.has(r.id));
            notifyRows.forEach(r => notifiedIdsRef.current.add(r.id));
            if (notifyRows.length > 0) showBrowserNotification(notifyRows);
          }
        }
        setUnseen(rows);
      } catch (err) {
        console.error('Erreur polling payment_deletion_audit:', err);
      }
    };

    // Demander la permission de notification une fois (silencieux si refusée)
    if ('Notification' in window && Notification.permission === 'default') {
      try {
        Notification.requestPermission();
      } catch (_) {}
    }

    poll();
    const interval = setInterval(poll, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [isSupervisor, user?.email]);

  // Réinitialiser les IDs notifiés navigateur quand on revient sur l'onglet
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        notifiedIdsRef.current.clear();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, []);

  const acquit = () => {
    if (!user?.email || unseen.length === 0) return;
    // Marque tout comme vu : le plus récent deleted_at devient le nouveau lastSeen
    const maxTimestamp = unseen.reduce(
      (max, r) => (r.deleted_at > max ? r.deleted_at : max),
      unseen[0].deleted_at
    );
    localStorage.setItem(LS_KEY(user.email), maxTimestamp);
    knownIdsRef.current.clear();
    setUnseen([]);
  };

  const handleAcquit = () => {
    acquit();
  };

  const handleViewDetails = () => {
    acquit();
    navigate(ROUTES.PAYMENT_DELETION_AUDIT);
  };

  if (!isSupervisor || unseen.length === 0) return null;

  return (
    <LuxModal
      isOpen={true}
      onClose={() => {
        /* no-op : oblige à cliquer un bouton pour acquitter */
      }}
      animationState="in"
      size="lg"
      variant="default"
      closeOnBackdrop={false}
      closeOnEscape={false}
      showCloseButton={false}
      className="!max-w-2xl"
    >
      <LuxModalHeader variant="default">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-red-100">
            <ShieldExclamationIcon className="w-5 h-5 text-red-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-ink">
              {unseen.length === 1
                ? "Suppression d'acompte détectée"
                : `${unseen.length} suppressions d'acompte détectées`}
            </h3>
            <p className="text-xs text-muted mt-0.5">
              Acquittement requis pour fermer cette alerte
            </p>
          </div>
        </div>
      </LuxModalHeader>

      <LuxModalBody className="!px-6 !py-3" style={{ backgroundColor: '#fafafa' }}>
        <ul className="space-y-2">
          {unseen.map(row => (
            <li
              key={row.id}
              className="rounded-token-lg border border-red-200 bg-red-50/60 px-3 py-2"
            >
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-ink truncate">
                    {row.order_number || '-'} · {(row.client_name || '-').toUpperCase()}
                  </div>
                  <div className="text-xs text-muted mt-0.5">
                    {formatDateTime(row.deleted_at)}
                    {row.payment_method ? ` · ${row.payment_method}` : ''}
                  </div>
                  <div className="text-xs text-red-700 mt-0.5">
                    Par {row.deleted_by_full_name || row.deleted_by_email || '?'}
                    {row.deleted_by_role ? ` (${row.deleted_by_role})` : ''}
                  </div>
                </div>
                <div className="text-base font-semibold text-red-700 flex-shrink-0">
                  {formatPrice(Number(row.amount) || 0)}
                </div>
              </div>
            </li>
          ))}
        </ul>
      </LuxModalBody>

      <LuxModalFooter align="right">
        <Button type="button" variant="secondary" size="md" onClick={handleAcquit}>
          Valider
        </Button>
        <Button type="button" variant="primary" size="md" onClick={handleViewDetails}>
          Voir tout dans Surveillance
        </Button>
      </LuxModalFooter>
    </LuxModal>
  );
};

export default PaymentDeletionWatcher;
